import { generateShopSlug, slugify } from "@/lib/slugify";
import { getEuropeCountryByCode, normalizeCountryCode } from "@shared/europe-countries";
import type { Shop } from "@shared/schema";

/**
 * Returns the localized URL prefix for a shop based on its country.
 * 
 * Rules requested by user:
 * - Always use the language of the country for SEO.
 */
/**
 * Returns the localized URL prefix for a shop based on its country.
 * 
 * Rules requested by user:
 * - Always use the language of the country for SEO.
 */
export function getShopUrlTypeSegment(countryCode?: string | null, locale?: string): string {
    const code = normalizeCountryCode(countryCode);

    const terms: Record<string, string> = {
        "NL": "kringloopwinkel",
        "BE": "kringloopwinkel",
        "FR": "magasin-doccasion",
        "DE": "second-hand-laden",
        "AT": "second-hand-laden",
        "CH": "second-hand-laden",
        "ES": "tienda-de-segunda-mano",
        "IT": "negozio-di-usato",
        "SE": "second-hand-butik",
        "DK": "genbrugsbutik",
        "UK": "second-hand-shop",
        "GB": "second-hand-shop",
        "IE": "second-hand-shop",
        "US": "thrift-store",
        "CA": "thrift-store",
        "AU": "thrift-store",
        "NZ": "thrift-store"
    };

    // If we have a recognized country code, use its native term
    if (code && terms[code]) {
        return terms[code];
    }

    // Fallback based on user locale if country is missing or unrecognized
    // This helps Dutch users see "kringloopwinkel" for shops without country data
    if (locale === 'nl' || (!locale && !code)) {
        return "kringloopwinkel";
    }

    if (locale === 'fr') return "magasin-doccasion";
    if (locale === 'de') return "second-hand-laden";
    if (locale === 'es') return "tienda-de-segunda-mano";
    if (locale === 'it') return "negozio-di-usato";
    if (locale === 'da') return "genbrugsbutik";
    if (locale === 'sv') return "second-hand-butik";

    return "thrift-store";
}

/**
 * Returns the country name segment for the URL (in native language)
 */
export function getCountrySegment(countryCode?: string | null): string {
    if (!countryCode) return "";
    const countryCountCode = normalizeCountryCode(countryCode);
    if (!countryCountCode) return "";

    const country = getEuropeCountryByCode(countryCountCode);
    if (country) {
        // Use local label if available, otherwise NL, then Fallback to EN
        return slugify(country.labelLocal || country.labelNl || country.labelEn);
    }
    return slugify(countryCountCode.toLowerCase());
}

/**
 * Generates the full URL for a shop profile
 * Format: /[country]/[type]/[province]/[city]/[name]
 */
export function getShopProfileUrl(shop: { id: string, slug?: string | null, province?: string | null, country?: string | null, city?: string | null, name?: string | null }, locale?: string): string {
    const typeSegment = getShopUrlTypeSegment(shop.country, locale);
    const countrySegment = getCountrySegment(shop.country);

    // Base prefix: /country/type or /type if country is missing
    const prefix = countrySegment ? `/${countrySegment}/${typeSegment}` : `/${typeSegment}`;

    // If we have a slug, use it
    if (shop.slug && shop.province) {
        return `${prefix}/${shop.slug}`;
    }

    // Fallback if slug is missing but we have details
    if (shop.province && (shop.city || (shop as any).city?.value) && (shop.name || (shop as any).name?.value)) {
        const city = typeof shop.city === 'string' ? shop.city : (shop.city as any)?.value || "unknown";
        const name = typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "shop";

        // Generate slug using standard util (province/city/name)
        const slug = generateShopSlug(shop.province, city, name);
        return `${prefix}/${slug}`;
    }

    // Ultimate fallback (backwards compatibility)
    return `/shops/${shop.id}`;
}
